import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { fetchGeo, getCachedGeo } from '@/lib/geo';
import { createBrowserId } from '@/lib/browserId';
import { getSessionId } from '@/lib/sessionId';
import { supabase } from '@/integrations/supabase/client';
import { getClientEnvPayload } from '@/lib/clientEnv';
import { getSupabaseFunctionUrl } from '@/lib/publicSupabaseConfig';
import {
  ANALYTICS_CONSENT_EVENT,
  hasAnalyticsConsent,
  trackProductEvent,
} from '@/lib/productAnalytics';

const getVisitorId = (): string => {
  let vid = localStorage.getItem('vs_visitor_id');
  if (!vid) {
    vid = createBrowserId();
    localStorage.setItem('vs_visitor_id', vid);
  }
  return vid;
};

const isReturningVisitor = (): boolean => {
  const visited = localStorage.getItem('vs_visited_before');
  if (!visited) {
    const now = Date.now().toString();
    localStorage.setItem('vs_visited_before', now);
    localStorage.setItem('vs_first_visit_at', new Date().toISOString());
    return false;
  }
  return true;
};

const getFirstVisitAt = (): string | null => localStorage.getItem('vs_first_visit_at');

const getFirstVisitStart = (): number => {
  const existing = sessionStorage.getItem('vs_first_visit_start_ms');
  if (existing) return parseInt(existing, 10);
  const now = Date.now();
  // Only mark a "first visit session" if this is actually the user's first visit ever
  const visitedBefore = localStorage.getItem('vs_visited_before');
  if (!visitedBefore || parseInt(visitedBefore, 10) > now - 5000) {
    sessionStorage.setItem('vs_first_visit_start_ms', now.toString());
    sessionStorage.setItem('vs_is_first_visit_session', '1');
  }
  return now;
};

const isFirstVisitSession = (): boolean => sessionStorage.getItem('vs_is_first_visit_session') === '1';

const getVisitCount = (): number => {
  const count = parseInt(localStorage.getItem('vs_visit_count') || '0', 10) + 1;
  localStorage.setItem('vs_visit_count', count.toString());
  return count;
};

const TRACK_URL = getSupabaseFunctionUrl('track-analytics');

// Global dedup + throttle to prevent flooding the edge function when
// useAnalytics() is mounted in many components simultaneously.
const recentEvents = new Map<string, number>();
const DEDUP_WINDOW_MS = 2000;
const TRACK_TIMEOUT_MS = 3000;
const HEARTBEAT_INTERVAL_MS = 120_000;

// Circuit breaker: if the edge function is failing, pause requests
// for a while instead of hammering it (which surfaces 503s as runtime errors).
let circuitOpenUntil = 0;
const CIRCUIT_COOLDOWN_MS = 5 * 60_000;
const TELEMETRY_PAUSE_KEY = 'vs_telemetry_pause_until_ms';

const telemetryPauseUntil = (): number => {
  try {
    return parseInt(sessionStorage.getItem(TELEMETRY_PAUSE_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
};

const openTelemetryCircuit = () => {
  const until = Date.now() + CIRCUIT_COOLDOWN_MS;
  circuitOpenUntil = until;
  try {
    sessionStorage.setItem(TELEMETRY_PAUSE_KEY, String(until));
  } catch {
    // sessionStorage may be unavailable in strict/privacy contexts.
  }
};

const isAutomationTelemetryContext = (): boolean => {
  if (typeof window === 'undefined') return true;
  if (navigator.webdriver) return true;
  const params = new URLSearchParams(window.location.search);
  for (const key of params.keys()) {
    if (key.startsWith('codex_') || key.startsWith('playwright_')) return true;
  }
  return false;
};

const shouldSkipProductTelemetry = (): boolean => {
  if (typeof window === 'undefined') return true;
  if (!hasAnalyticsConsent()) return true;
  if (window.location.pathname.startsWith('/admin')) return true;
  if (isAutomationTelemetryContext()) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return false;
};

const shouldSkipTelemetry = (): boolean => {
  if (shouldSkipProductTelemetry()) return true;
  if (!TRACK_URL) return true;
  const now = Date.now();
  return now < circuitOpenUntil || now < telemetryPauseUntil();
};

const isDuplicateEvent = (eventName: string, eventType: string) => {
  const key = `${eventType}:${eventName}:${window.location.pathname}`;
  const now = Date.now();
  const last = recentEvents.get(key) || 0;
  if (now - last < DEDUP_WINDOW_MS) return true;
  recentEvents.set(key, now);
  if (recentEvents.size > 50) {
    for (const [storedKey, timestamp] of recentEvents) {
      if (now - timestamp > DEDUP_WINDOW_MS * 5) recentEvents.delete(storedKey);
    }
  }
  return false;
};

const sendToTracker = async (payload: Record<string, unknown>, accessToken?: string) => {
  try {
    if (shouldSkipTelemetry()) return;

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), TRACK_TIMEOUT_MS);
    const res = await fetch(TRACK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
      signal: controller.signal,
    }).catch(() => null);
    window.clearTimeout(timeout);

    // Open the circuit on edge runtime / server errors so the next
    // batch of events doesn't repeatedly trip the same failure.
    if (!res || res.status === 503 || res.status === 504 || res.status >= 500) {
      openTelemetryCircuit();
    }
  } catch {
    // silent fail — analytics must never break the app
    openTelemetryCircuit();
  }
};

export const trackEvent = async (
  eventName: string,
  eventType: string = 'click',
  eventData: Record<string, unknown> = {},
  userId?: string
) => {
  if (shouldSkipProductTelemetry()) return;
  if (isDuplicateEvent(eventName, eventType)) return;

  // Ensure first-visit session tracking is initialized
  getFirstVisitStart();

  const geo = getCachedGeo();
  const env = getClientEnvPayload();
  const enrichedData = {
    ...eventData,
    visitor_id: getVisitorId(),
    is_returning: isReturningVisitor(),
    visit_count: parseInt(localStorage.getItem('vs_visit_count') || '1', 10),
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    first_visit_at: getFirstVisitAt(),
    is_first_visit_session: isFirstVisitSession(),
    client_env: env,
    ...(geo
      ? {
          geo_country: geo.country,
          geo_country_code: geo.country_code,
          geo_region: geo.region,
          geo_city: geo.city,
          geo_postal: geo.postal,
          geo_lat: geo.lat,
          geo_lng: geo.lng,
        }
      : {}),
  };

  let accessToken: string | undefined;
  if (userId) {
    try {
      const { data } = await supabase.auth.getSession();
      accessToken = data.session?.access_token;
    } catch {
      accessToken = undefined;
    }
  }

  await sendToTracker({
    user_id: userId || null,
    session_id: getSessionId(),
    event_type: eventType,
    event_name: eventName,
    event_data: enrichedData,
    page_url: window.location.pathname,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
  }, accessToken);
  await trackProductEvent(eventName, {
    event_type: eventType,
    page_path: window.location.pathname,
    ...eventData,
  }, userId);
};

export const useAnalytics = () => {
  const { user } = useAuth();
  const [analyticsAllowed, setAnalyticsAllowed] = useState(hasAnalyticsConsent);
  const tracked = useRef(false);
  const sessionStart = useRef(Date.now());
  const scrollMax = useRef(0);

  const track = useCallback(
    (eventName: string, eventType = 'click', eventData: Record<string, unknown> = {}) => {
      if (window.location.pathname.startsWith('/admin')) return;
      trackEvent(eventName, eventType, eventData, user?.id);
    },
    [user?.id]
  );

  useEffect(() => {
    const syncConsent = () => {
      const allowed = hasAnalyticsConsent();
      if (!allowed) tracked.current = false;
      setAnalyticsAllowed(allowed);
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, syncConsent);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, syncConsent);
  }, []);

  // Auto-track page views with enriched data.
  // Deferred to requestIdleCallback so the initial page_view request never
  // sits on the LCP critical path (was adding ~500ms on slow networks).
  useEffect(() => {
    if (shouldSkipProductTelemetry()) return;
    if (tracked.current) return;
    tracked.current = true;
    const fire = () => {
      const visitCount = getVisitCount();
      trackEvent('page_view', 'pageview', {
        path: window.location.pathname,
        visit_count: visitCount,
        is_returning: visitCount > 1,
        entry_page: window.location.pathname,
      }, user?.id);
    };
    // Kick off geo lookup in background; page_view fires regardless.
    fetchGeo().catch(() => {});
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === 'function') ric(fire, { timeout: 2500 });
    else setTimeout(fire, 1500);
  }, [analyticsAllowed, user?.id]);

  // Track scroll depth
  useEffect(() => {
    if (shouldSkipProductTelemetry()) return;
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > scrollMax.current) {
        scrollMax.current = scrollPercent;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [analyticsAllowed]);

  // Track session duration & scroll depth on page unload
  useEffect(() => {
    if (shouldSkipProductTelemetry()) return;
    const handleUnload = () => {
      if (shouldSkipTelemetry()) return;
      const duration = Math.round((Date.now() - sessionStart.current) / 1000);
      const firstVisitStart = parseInt(sessionStorage.getItem('vs_first_visit_start_ms') || '0', 10);
      const firstVisit = isFirstVisitSession();
      const data = {
        duration_seconds: duration,
        max_scroll_percent: scrollMax.current,
        pages_in_session: parseInt(sessionStorage.getItem('vs_pages_count') || '1', 10),
        is_first_visit_session: firstVisit,
        first_visit_duration_seconds: firstVisit && firstVisitStart
          ? Math.round((Date.now() - firstVisitStart) / 1000)
          : null,
        first_visit_at: getFirstVisitAt(),
      };
      const payload = JSON.stringify({
        user_id: user?.id || null,
        session_id: getSessionId(),
        event_type: 'session',
        event_name: 'session_end',
        event_data: data,
        page_url: window.location.pathname,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
      });
      navigator.sendBeacon(TRACK_URL, new Blob([payload], { type: 'application/json' }));
    };

    // Track pages count in session
    const count = parseInt(sessionStorage.getItem('vs_pages_count') || '0', 10) + 1;
    sessionStorage.setItem('vs_pages_count', count.toString());

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [analyticsAllowed, user?.id]);

  // Track time on page every 30 seconds (engagement heartbeat)
  // Only one hook instance globally runs the heartbeat to avoid duplicate events.
  useEffect(() => {
    if (shouldSkipProductTelemetry()) return;
    const w = window as unknown as { __vsHeartbeatOwner?: number };
    const myId = Date.now() + Math.random();
    if (w.__vsHeartbeatOwner) return; // another instance already owns it
    w.__vsHeartbeatOwner = myId;

    const interval = setInterval(() => {
      const timeOnPage = Math.round((Date.now() - sessionStart.current) / 1000);
      if (timeOnPage >= 60) {
        trackEvent('engagement_heartbeat', 'engagement', {
          time_on_page: timeOnPage,
          scroll_depth: scrollMax.current,
        }, user?.id);
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      if (w.__vsHeartbeatOwner === myId) delete w.__vsHeartbeatOwner;
    };
  }, [analyticsAllowed, user?.id]);

  return { track };
};
